import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// One shared handoff request per (ref,code) — dedupes React StrictMode double-mounts and effect reruns,
// so a single short ref+code is exchanged exactly once even if the effect fires multiple times.
const inflightHandoff = new Map();
function handoffOnce(ref, code) {
  const key = `${ref}::${code}`;
  if (!inflightHandoff.has(key)) {
    const p = api.post("/auth/ez-handoff", { ref, code })
      .then((res) => res.data)
      .finally(() => inflightHandoff.delete(key));
    inflightHandoff.set(key, p);
  }
  return inflightHandoff.get(key);
}

// Lands students coming from EntranceZone Editorial Intelligence: /ez?ref=..&code=..
// The short ref+code is exchanged server-side; the signed handoff JWT never appears in the URL.
export default function EzHandoffPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const ref = params.get("ref");
    const code = params.get("code");
    if (!ref || !code) {
      setError("This link is incomplete. Please go back to EntranceZone and click Continue again.");
      return;
    }
    handoffOnce(ref, code)
      .then((data) => {
        // Adopt ONLY the verified handoff user's session; replaces any stale token/user.
        adoptSession(data);
        navigate(data.assessment_completed ? "/dashboard" : "/assessment", { replace: true });
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't confirm your EntranceZone session."));
  }, [params, navigate, adoptSession]);

  return (
    <div data-testid="ez-handoff-page" className="min-h-screen bg-[#0B0F17] text-white flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl border border-white/10 bg-[#131C2E] p-8 text-center">
        {!error ? (
          <>
            <Loader2 data-testid="ez-handoff-loading" className="mx-auto h-8 w-8 animate-spin text-cyan-400" />
            <h1 className="mt-4 font-display text-xl font-bold">Connecting your EntranceZone account…</h1>
            <p className="mt-2 text-sm text-slate-400 flex items-center justify-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Secure handoff in progress</p>
          </>
        ) : (
          <>
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-400" />
            <h1 className="mt-4 font-display text-xl font-bold">We couldn't connect your account</h1>
            <p data-testid="ez-handoff-error" className="mt-2 text-sm text-slate-400">{error}</p>
            <Link data-testid="ez-handoff-fallback" to="/auth" className="mt-6 inline-block rounded-full bg-cyan-400 px-6 py-2.5 text-sm font-bold text-[#041016]">Sign in to EntranceZone AI</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
